import { useCallback, useEffect, useMemo, useState } from 'react'
import { UsePickerProps } from './props'

const usePicker = <MT extends React.MouseEvent | React.TouchEvent>(
    props: UsePickerProps
) => {
    const { itemHeight, onChange, options, pickerHeight, value } = props

    const [canMove, setCanMove] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [dragStart, setDragStart] = useState({
        clientYStart: 0,
        translateStart: 0,
    })
    const [isMoving, setIsMoving] = useState(false)
    const [translate, setTranslate] = useState(0)

    // Maximum and mimium scrolling
    const translateMax = useMemo(
        () => pickerHeight / 2 - itemHeight / 2,
        [itemHeight, pickerHeight]
    )
    const translateMin = useMemo(
        () => pickerHeight / 2 - itemHeight * options.length + itemHeight / 2,
        [itemHeight, options.length, pickerHeight]
    )

    const setNewIndex = useCallback(
        (index: number, skipOnChange?: boolean) => {
            const newIndex =
                index < 0
                    ? 0
                    : index > options.length - 1
                    ? options.length - 1
                    : index

            setTranslate(
                pickerHeight / 2 - itemHeight / 2 - newIndex * itemHeight
            )
            setCurrentIndex(newIndex)
            if (!skipOnChange) onChange?.(options[newIndex].value)
        },
        [itemHeight, onChange, options, pickerHeight]
    )

    // Onmount choose value if selected or first option
    useEffect(() => {
        const index = options.findIndex((opt) => opt.value === value)
        setNewIndex(index !== -1 ? index : 0, true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemHeight, options, pickerHeight, value])

    const getClientY = useCallback(({ nativeEvent: ne }: MT) => {
        if (ne instanceof MouseEvent) return ne.clientY
        if (ne instanceof TouchEvent) return ne.touches[0].clientY
        return 0
    }, [])

    const startDrag = useCallback(
        (e: MT) => {
            setCanMove(true)

            // Set start values for scrolling
            setDragStart({
                clientYStart: getClientY(e),
                translateStart: translate,
            })
        },
        [getClientY, translate]
    )

    const moveDrag = useCallback(
        (e: MT) => {
            if (!canMove) return

            setIsMoving(true)

            const { clientYStart, translateStart } = dragStart
            const newTranslate = translateStart + getClientY(e) - clientYStart

            // Makes it feel like it gets harder to pull
            setTranslate(
                newTranslate < translateMin
                    ? translateMin - Math.pow(translateMin - newTranslate, 0.8)
                    : newTranslate > translateMax
                    ? translateMax + Math.pow(newTranslate - translateMax, 0.8)
                    : newTranslate
            )
        },
        [canMove, dragStart, getClientY, translateMax, translateMin]
    )

    const stopDrag = useCallback(
        (e: MT) => {
            // Decide index of options
            const index = isMoving
                ? -Math.round((translate - translateMax) / itemHeight)
                : (e.target as HTMLElement).offsetTop / itemHeight

            setNewIndex(index)
            setIsMoving(false)
            setCanMove(false)
        },
        [isMoving, itemHeight, setNewIndex, translate, translateMax]
    )

    const wheelDrag = useCallback(
        (e: React.WheelEvent) => {
            e.stopPropagation()
            if (isMoving) return

            // Decide index of options
            const scrollIndex = e.deltaY > 0 ? 1 : -1
            const index = currentIndex + scrollIndex

            setNewIndex(index)
        },
        [currentIndex, isMoving, setNewIndex]
    )

    return {
        currentIndex,
        draggers: { moveDrag, startDrag, stopDrag, wheelDrag },
        isMoving,
        translate,
    }
}

export default usePicker
